import React, { useMemo } from "react";
import ThreadGroup from "./ThreadGroup";

const SequenceBuilder = ({ steps, onChange, subject, onSubjectChange }) => {
    // Compute groups from the flat steps array.
    // A "thread" step marks the start of a new group.
    const groups = useMemo(() => {
        const result = [];
        let current = { threadFlatIndex: null, groupSteps: [] };

        steps.forEach((step, i) => {
            if (step.type === "thread") {
                result.push(current);
                current = { threadFlatIndex: i, groupSteps: [] };
            } else {
                current.groupSteps.push({ step, flatIndex: i });
            }
        });
        result.push(current);
        return result;
    }, [steps]);

    // Global step numbers (thread steps are not counted)
    const globalStepNumber = useMemo(() => {
        const map = {};
        let n = 0;
        steps.forEach((s, i) => {
            if (s.type !== "thread") map[i] = ++n;
        });
        return map;
    }, [steps]);
    
    const addStepToGroup = (groupIndex, type) => {
        const group = groups[groupIndex];
        const newStep = type === "email" ? { type: "email", body: "" } : { type: "delay", delayDays: 2 };
        // Insert after the last step of this group, or right after the thread marker if empty
        const insertAt = group.groupSteps.length > 0
            ? group.groupSteps[group.groupSteps.length - 1].flatIndex + 1
            : (group.threadFlatIndex !== null ? group.threadFlatIndex + 1 : 0);
        const next = [...steps];
        next.splice(insertAt, 0, newStep);
        onChange(next);
    };

    const updateStep = (flatIndex, updated) => {
        const next = [...steps];
        next[flatIndex] = updated;
        onChange(next);
    };

    const removeStep = (flatIndex) => {
        onChange(steps.filter((_, i) => i !== flatIndex));
    };

    const moveUp = (flatIndex) => {
        if (flatIndex === 0) return;
        const next = [...steps];
        [next[flatIndex - 1], next[flatIndex]] = [next[flatIndex], next[flatIndex - 1]];
        onChange(next);
    };

    const moveDown = (flatIndex) => {
        if (flatIndex === steps.length - 1) return;
        const next = [...steps];
        [next[flatIndex], next[flatIndex + 1]] = [next[flatIndex + 1], next[flatIndex]];
        onChange(next);
    };

    const removeThread = (threadFlatIndex) => {
        // Remove the thread marker — steps naturally merge into the previous group
        onChange(steps.filter((_, i) => i !== threadFlatIndex));
    };

    const updateThreadSubject = (threadFlatIndex, newSubject) => {
        const next = [...steps];
        next[threadFlatIndex] = { ...next[threadFlatIndex], subject: newSubject };
        onChange(next);
    };

    const addThread = () => {
        onChange([...steps, { type: "thread", subject: "" }]);
    };

    return (
        <div className="crm-sequence-groups">
            {groups.map((group, groupIndex) => (
                <ThreadGroup
                    key={groupIndex}
                    groupIndex={groupIndex}
                    threadSubject={groupIndex === 0 ? (subject || "") : (steps[group.threadFlatIndex]?.subject || "")}
                    onThreadSubjectChange={(v) =>
                        groupIndex === 0 ? onSubjectChange(v) : updateThreadSubject(group.threadFlatIndex, v)
                    }
                    groupSteps={group.groupSteps}
                    globalStepNumber={globalStepNumber}
                    totalFlatSteps={steps.length}
                    canRemove={groupIndex > 0}
                    onRemoveThread={() => removeThread(group.threadFlatIndex)}
                    onAddStep={(type) => addStepToGroup(groupIndex, type)}
                    onUpdateStep={updateStep}
                    onRemoveStep={removeStep}
                    onMoveUp={moveUp}
                    onMoveDown={moveDown}
                />
            ))}

            <div className="crm-sequence-new-thread-row">
                <button className="admin-btn" onClick={addThread} type="button">
                    + New Thread
                </button>
            </div>
        </div>
    );
};

export default SequenceBuilder;
